import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Toaster, toast } from 'sonner';
import { 
  CheckCircle2, Save, User, Building2, Calendar, 
  ClipboardCheck, AlertTriangle, Users, Plus, Trash2
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Mois de suivi
const months = [
  'Septembre 2026', 'Octobre 2026', 'Novembre 2026', 'Décembre 2026',
  'Janvier 2027', 'Février 2027', 'Mars 2027', 'Avril 2027',
  'Mai 2027', 'Juin 2027', 'Juillet 2027'
];

// Compétences du référentiel
const competences = [
  { id: 'c1', label: 'Assurer une veille commerciale' },
  { id: 'c2', label: 'Concevoir et organiser un plan d\'actions commerciales' },
  { id: 'c3', label: 'Prospecter un secteur défini' },
  { id: 'c4', label: 'Analyser ses performances commerciales' },
  { id: 'c5', label: 'Représenter l\'entreprise et valoriser son image' },
  { id: 'c6', label: 'Concevoir une proposition technique et commerciale' },
  { id: 'c7', label: 'Négocier une solution technique et commerciale' },
  { id: 'c8', label: 'Réaliser le bilan de son activité commerciale' },
  { id: 'c9', label: 'Optimiser la gestion de la relation client' }
];

export const SuiviPedagogique = () => {
  const [activeTab, setActiveTab] = useState('identification');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState('Septembre 2026');
  
  const [formData, setFormData] = useState({
    // Identification
    apprentiName: '',
    maitreApprentissage: '',
    entreprise: '',
    formateurReferent: '',
    promotionStart: '',
    promotionEnd: '',
    // Suivi mensuel (par mois)
    monthlyTracking: {},
    // Réunions triadiques
    triadicMeetings: [
      { period: 'T1 – Décembre 2026', points: '', actions: '', done: false },
      { period: 'T2 – Mars 2027', points: '', actions: '', done: false },
      { period: 'T3 – Juin 2027', points: '', actions: '', done: false }
    ],
    // Absences et difficultés
    absences: []
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleMonthlyChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      monthlyTracking: {
        ...prev.monthlyTracking,
        [selectedMonth]: {
          ...prev.monthlyTracking[selectedMonth],
          [field]: value
        }
      }
    }));
  };

  const handleCompetenceToggle = (competenceId) => {
    const currentCompetences = formData.monthlyTracking[selectedMonth]?.competences || [];
    const newCompetences = currentCompetences.includes(competenceId)
      ? currentCompetences.filter(c => c !== competenceId)
      : [...currentCompetences, competenceId];
    
    handleMonthlyChange('competences', newCompetences);
  };

  const handleTriadicChange = (index, field, value) => {
    const newMeetings = [...formData.triadicMeetings];
    newMeetings[index] = { ...newMeetings[index], [field]: value };
    setFormData(prev => ({ ...prev, triadicMeetings: newMeetings }));
  };

  const addAbsence = () => {
    setFormData(prev => ({
      ...prev,
      absences: [...prev.absences, { date: '', motif: '', mesure: '', responsable: '', resolution: '' }]
    }));
  };

  const removeAbsence = (index) => {
    setFormData(prev => ({
      ...prev,
      absences: prev.absences.filter((_, i) => i !== index)
    }));
  };

  const handleAbsenceChange = (index, field, value) => {
    const newAbsences = [...formData.absences];
    newAbsences[index] = { ...newAbsences[index], [field]: value };
    setFormData(prev => ({ ...prev, absences: newAbsences }));
  };

  const handleSave = async () => {
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/suivi-pedagogique`, formData);
      toast.success('Livret de suivi enregistré !', {
        description: 'Vos informations ont été sauvegardées.'
      });
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement', {
        description: 'Veuillez réessayer plus tard.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const tabs = [
    { id: 'identification', label: 'Identification', icon: User },
    { id: 'monthly', label: 'Suivi mensuel', icon: Calendar },
    { id: 'triadic', label: 'Réunions triadiques', icon: Users },
    { id: 'absences', label: 'Absences / Difficultés', icon: AlertTriangle }
  ];

  return (
    <div data-testid="suivi-page">
      <Toaster position="top-right" richColors />
      
      {/* Hero */}
      <section className="bg-[#0B1F3A] py-16">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div>
              <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#C8A75A] mb-4 block">
                Espace Étudiant
              </span>
              <h1 className="font-serif-heading text-3xl sm:text-4xl font-semibold text-white mb-2">
                Livret de suivi pédagogique
              </h1>
              <p className="text-[#F5F5F5]/80">
                TP Négociateur Technico-Commercial – RNCP39063
              </p>
            </div>
            <Button 
              onClick={handleSave}
              disabled={isSubmitting}
              className="mt-6 md:mt-0 bg-[#C8A75A] text-[#0B1F3A] hover:bg-[#DDBF78] rounded-none px-6"
              data-testid="save-button"
            >
              <Save className="mr-2" size={18} />
              {isSubmitting ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <section className="bg-white border-b border-[#E6E6E6]">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="flex overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-4 border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-[#C8A75A] text-[#0B1F3A] font-medium'
                    : 'border-transparent text-[#4A4A4A] hover:text-[#0B1F3A]'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon size={18} />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 bg-[#F5F5F5]">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          
          {/* Identification Tab */}
          {activeTab === 'identification' && (
            <div className="bg-white p-8 border border-[#E6E6E6]" data-testid="tab-content-identification">
              <h2 className="font-serif-heading text-xl font-semibold text-[#0B1F3A] mb-6 flex items-center gap-2">
                <User className="text-[#C8A75A]" size={24} />
                Identification
              </h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-sm font-semibold text-[#0B1F3A] uppercase tracking-wide">Apprenti(e)</Label>
                  <Input
                    name="apprentiName"
                    value={formData.apprentiName}
                    onChange={handleInputChange}
                    className="mt-2 rounded-none"
                    placeholder="Nom Prénom"
                    data-testid="input-apprenti"
                  />
                </div>
                <div>
                  <Label className="text-sm font-semibold text-[#0B1F3A] uppercase tracking-wide">Maître d'apprentissage</Label>
                  <Input
                    name="maitreApprentissage"
                    value={formData.maitreApprentissage}
                    onChange={handleInputChange}
                    className="mt-2 rounded-none"
                    placeholder="Nom Prénom"
                    data-testid="input-maitre"
                  />
                </div>
                <div>
                  <Label className="text-sm font-semibold text-[#0B1F3A] uppercase tracking-wide">Entreprise</Label>
                  <Input
                    name="entreprise"
                    value={formData.entreprise}
                    onChange={handleInputChange}
                    className="mt-2 rounded-none"
                    placeholder="Nom de l'entreprise"
                    data-testid="input-entreprise"
                  />
                </div>
                <div>
                  <Label className="text-sm font-semibold text-[#0B1F3A] uppercase tracking-wide">Formateur référent</Label>
                  <Input
                    name="formateurReferent"
                    value={formData.formateurReferent}
                    onChange={handleInputChange}
                    className="mt-2 rounded-none"
                    placeholder="Nom Prénom"
                    data-testid="input-formateur"
                  />
                </div>
                <div>
                  <Label className="text-sm font-semibold text-[#0B1F3A] uppercase tracking-wide">Début de promotion</Label>
                  <Input
                    name="promotionStart"
                    type="date"
                    value={formData.promotionStart}
                    onChange={handleInputChange}
                    className="mt-2 rounded-none"
                    data-testid="input-start"
                  />
                </div>
                <div>
                  <Label className="text-sm font-semibold text-[#0B1F3A] uppercase tracking-wide">Fin de promotion</Label>
                  <Input
                    name="promotionEnd"
                    type="date"
                    value={formData.promotionEnd}
                    onChange={handleInputChange}
                    className="mt-2 rounded-none"
                    data-testid="input-end"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Monthly Tracking Tab */}
          {activeTab === 'monthly' && (
            <div className="bg-white p-8 border border-[#E6E6E6]" data-testid="tab-content-monthly">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
                <h2 className="font-serif-heading text-xl font-semibold text-[#0B1F3A] flex items-center gap-2">
                  <Calendar className="text-[#C8A75A]" size={24} />
                  Suivi mensuel des compétences
                </h2>
                <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                  <SelectTrigger className="w-[200px] rounded-none mt-4 md:mt-0" data-testid="select-month">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {months.map((month) => (
                      <SelectItem key={month} value={month}>{month}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Competences checkboxes */}
              <div className="mb-6">
                <Label className="text-sm font-semibold text-[#0B1F3A] uppercase tracking-wide mb-4 block">
                  Compétences travaillées ce mois
                </Label>
                <div className="grid md:grid-cols-2 gap-3">
                  {competences.map((comp) => (
                    <div key={comp.id} className="flex items-center space-x-3 p-3 bg-[#F5F5F5]">
                      <Checkbox
                        id={comp.id}
                        checked={(formData.monthlyTracking[selectedMonth]?.competences || []).includes(comp.id)}
                        onCheckedChange={() => handleCompetenceToggle(comp.id)}
                      />
                      <Label htmlFor={comp.id} className="text-sm cursor-pointer">{comp.label}</Label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-sm font-semibold text-[#0B1F3A] uppercase tracking-wide">Présences</Label>
                  <Select 
                    value={formData.monthlyTracking[selectedMonth]?.presences || ''}
                    onValueChange={(value) => handleMonthlyChange('presences', value)}
                  >
                    <SelectTrigger className="mt-2 rounded-none">
                      <SelectValue placeholder="Sélectionnez" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="100">100% - Aucune absence</SelectItem>
                      <SelectItem value="75-99">75-99% - Quelques absences justifiées</SelectItem>
                      <SelectItem value="50-74">50-74% - Absences fréquentes</SelectItem>
                      <SelectItem value="<50">&lt;50% - Absences critiques</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm font-semibold text-[#0B1F3A] uppercase tracking-wide">Évaluation formative</Label>
                  <Select 
                    value={formData.monthlyTracking[selectedMonth]?.evaluation || ''}
                    onValueChange={(value) => handleMonthlyChange('evaluation', value)}
                  >
                    <SelectTrigger className="mt-2 rounded-none">
                      <SelectValue placeholder="Sélectionnez" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="excellent">Excellent - Dépasse les attentes</SelectItem>
                      <SelectItem value="satisfaisant">Satisfaisant - Conforme aux attentes</SelectItem>
                      <SelectItem value="en-cours">En cours d'acquisition</SelectItem>
                      <SelectItem value="insuffisant">Insuffisant - À renforcer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <Label className="text-sm font-semibold text-[#0B1F3A] uppercase tracking-wide">Observations</Label>
                  <Textarea
                    value={formData.monthlyTracking[selectedMonth]?.observations || ''}
                    onChange={(e) => handleMonthlyChange('observations', e.target.value)}
                    className="mt-2 rounded-none"
                    rows={4}
                    placeholder="Commentaires sur la progression, points forts, axes d'amélioration..."
                  />
                </div>
              </div>
            </div>
          )}

          {/* Triadic Meetings Tab */}
          {activeTab === 'triadic' && (
            <div className="bg-white p-8 border border-[#E6E6E6]" data-testid="tab-content-triadic">
              <h2 className="font-serif-heading text-xl font-semibold text-[#0B1F3A] mb-6 flex items-center gap-2">
                <Users className="text-[#C8A75A]" size={24} />
                Réunions triadiques (OF / Apprenti / Entreprise)
              </h2>

              <div className="space-y-6">
                {formData.triadicMeetings.map((meeting, index) => (
                  <div key={index} className="border border-[#E6E6E6] p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-[#0B1F3A]">{meeting.period}</h3>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id={`meeting-done-${index}`}
                          checked={meeting.done}
                          onCheckedChange={(checked) => handleTriadicChange(index, 'done', checked)}
                        />
                        <Label htmlFor={`meeting-done-${index}`} className="text-sm">Réunion effectuée</Label>
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm font-semibold text-[#0B1F3A] uppercase tracking-wide">Points abordés</Label>
                        <Textarea
                          value={meeting.points}
                          onChange={(e) => handleTriadicChange(index, 'points', e.target.value)}
                          className="mt-2 rounded-none"
                          rows={3}
                          placeholder="Sujets discutés..."
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-semibold text-[#0B1F3A] uppercase tracking-wide">Actions décidées</Label>
                        <Textarea
                          value={meeting.actions}
                          onChange={(e) => handleTriadicChange(index, 'actions', e.target.value)}
                          className="mt-2 rounded-none"
                          rows={3}
                          placeholder="Décisions et prochaines étapes..."
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Absences Tab */}
          {activeTab === 'absences' && (
            <div className="bg-white p-8 border border-[#E6E6E6]" data-testid="tab-content-absences">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-serif-heading text-xl font-semibold text-[#0B1F3A] flex items-center gap-2">
                  <AlertTriangle className="text-[#C8A75A]" size={24} />
                  Gestion des absences et difficultés
                </h2>
                <Button onClick={addAbsence} variant="outline" className="rounded-none" data-testid="add-absence">
                  <Plus className="mr-2" size={18} /> Ajouter
                </Button>
              </div>

              {formData.absences.length === 0 ? (
                <div className="text-center py-12 text-[#4A4A4A]">
                  <ClipboardCheck className="mx-auto mb-4 text-[#C8A75A]" size={48} />
                  <p>Aucune absence ou difficulté enregistrée</p>
                  <p className="text-sm">Cliquez sur "Ajouter" pour signaler un événement</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {formData.absences.map((absence, index) => (
                    <div key={index} className="border border-[#E6E6E6] p-4">
                      <div className="flex justify-between items-start mb-4">
                        <span className="text-sm font-semibold text-[#0B1F3A]">Événement #{index + 1}</span>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => removeAbsence(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                      <div className="grid md:grid-cols-3 gap-4">
                        <div>
                          <Label className="text-xs font-semibold text-[#4A4A4A] uppercase">Date</Label>
                          <Input
                            type="date"
                            value={absence.date}
                            onChange={(e) => handleAbsenceChange(index, 'date', e.target.value)}
                            className="mt-1 rounded-none"
                          />
                        </div>
                        <div>
                          <Label className="text-xs font-semibold text-[#4A4A4A] uppercase">Motif</Label>
                          <Input
                            value={absence.motif}
                            onChange={(e) => handleAbsenceChange(index, 'motif', e.target.value)}
                            className="mt-1 rounded-none"
                            placeholder="Raison..."
                          />
                        </div>
                        <div>
                          <Label className="text-xs font-semibold text-[#4A4A4A] uppercase">Responsable</Label>
                          <Input
                            value={absence.responsable}
                            onChange={(e) => handleAbsenceChange(index, 'responsable', e.target.value)}
                            className="mt-1 rounded-none"
                            placeholder="Qui gère..."
                          />
                        </div>
                        <div className="md:col-span-2">
                          <Label className="text-xs font-semibold text-[#4A4A4A] uppercase">Mesure prise</Label>
                          <Input
                            value={absence.mesure}
                            onChange={(e) => handleAbsenceChange(index, 'mesure', e.target.value)}
                            className="mt-1 rounded-none"
                            placeholder="Action entreprise..."
                          />
                        </div>
                        <div>
                          <Label className="text-xs font-semibold text-[#4A4A4A] uppercase">Résolution</Label>
                          <Select 
                            value={absence.resolution}
                            onValueChange={(value) => handleAbsenceChange(index, 'resolution', value)}
                          >
                            <SelectTrigger className="mt-1 rounded-none">
                              <SelectValue placeholder="Statut" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="resolved">Résolu</SelectItem>
                              <SelectItem value="in-progress">En cours</SelectItem>
                              <SelectItem value="pending">En attente</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

        </div>
      </section>
    </div>
  );
};

export default SuiviPedagogique;
